import React from "react";

/**
 * DateMaskedInput — text input with MM/DD/YYYY mask.
 *
 * - Displays "MM/DD/YYYY" as placeholder (uppercase, on-brand).
 * - Auto-inserts "/" while the user types.
 * - Stores ISO format (YYYY-MM-DD) in `value` prop and emits it via `onChange`.
 * - Allows backspace/edit naturally; max 10 chars (MM/DD/YYYY).
 */
export default function DateMaskedInput({
  value = "",
  onChange,
  className = "",
  required = false,
  testid,
  ...rest
}) {
  // Convert ISO (YYYY-MM-DD) -> display (MM/DD/YYYY)
  const display = isoToDisplay(value);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9/]/g, "");
    // Auto-insert slashes as the user types
    let formatted = raw;

    // Strip slashes to count digits
    const digits = raw.replace(/\//g, "");
    if (digits.length <= 2) {
      formatted = digits;
    } else if (digits.length <= 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }

    // Emit ISO when complete, else emit empty string so backend validation kicks in only on submit
    const iso = displayToIso(formatted);
    onChange(iso ?? formatted); // fall back to display string if not yet complete
    // Note: we set the underlying input value below using the formatted display
    e.target.value = formatted;
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="bday"
      placeholder="MM/DD/YYYY"
      value={display}
      onChange={handleChange}
      maxLength={10}
      pattern="\d{2}/\d{2}/\d{4}"
      required={required}
      data-testid={testid}
      className={className}
      {...rest}
    />
  );
}

function isoToDisplay(v) {
  if (!v) return "";
  // ISO YYYY-MM-DD → MM/DD/YYYY
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  // Already in display format (or partial typing) — return as-is
  return v;
}

function displayToIso(v) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (!m) return null;
  const mm = parseInt(m[1], 10);
  const dd = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return null;
  if (dd < 1 || dd > 31) return null;
  if (yyyy < 1900 || yyyy > new Date().getFullYear()) return null;
  return `${m[3]}-${m[1]}-${m[2]}`;
}
